import { AlertCircle, CalendarDays, CheckCircle2, ChevronDown, ChevronUp, ChevronRight, FileUp, LoaderCircle, X } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { formatSlotDate, formatSlotTime, groupSlotsByDay } from "../lib/date";
import type { AvailabilitySlot, RequesterRole } from "../types";
import { viewUrl } from "../App";
import { Turnstile } from "./Turnstile";
import { WhatsAppCta, WhatsAppIcon } from "./WhatsAppCta";

function compactSlotTime(start: string, end: string) {
  return formatSlotTime(start, end).replace(/\sUhr$/, "");
}

export function BookingSection() {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [selected, setSelected] = useState<AvailabilitySlot>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [attachment, setAttachment] = useState<File>();
  const [role, setRole] = useState<RequesterRole>("parent");
  const [studentAge, setStudentAge] = useState("");
  const [bookingEmail, setBookingEmail] = useState("");
  const [bookingPhone, setBookingPhone] = useState("");
  const [guardianNameValue, setGuardianNameValue] = useState("");
  const [guardianEmailValue, setGuardianEmailValue] = useState("");
  const [guardianPhoneValue, setGuardianPhoneValue] = useState("");
  const [scrollToCalendarOnOpen, setScrollToCalendarOnOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  function changeStudentAge(value: string) {
    setStudentAge(value);
    const age = Number(value);
    if (Number.isInteger(age) && age < 18) {
      setBookingEmail("");
      setBookingPhone("");
      setGuardianNameValue("");
      setGuardianEmailValue("");
      setGuardianPhoneValue("");
    }
  }

  function selectAttachment(file?: File) {
    if (file && file.size > 10 * 1024 * 1024) {
      setAttachment(undefined);
      setError("Die Datei darf maximal 10 MB groß sein.");
      return;
    }
    setError("");
    setAttachment(file);
  }

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setSlots(await api.availability());
    } catch (caught) {
      const message = caught instanceof ApiError && caught.code === "calendar_not_connected"
        ? "Der Terminkalender wird gerade eingerichtet. Schreib mir gern direkt per WhatsApp."
        : caught instanceof Error ? caught.message : "Die freien Zeiten konnten nicht geladen werden.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const grouped = useMemo(() => groupSlotsByDay(slots), [slots]);
  const isMinorStudent = role === "student" && studentAge !== "" && Number(studentAge) < 18;

  function openCalendar() {
    setCalendarOpen(true);
    if (!loading && slots.length === 0) void loadSlots();
  }

  useEffect(() => {
    const openBookingCalendar = () => {
      setScrollToCalendarOnOpen(true);
      openCalendar();
    };
    window.addEventListener("booking:open", openBookingCalendar);
    return () => window.removeEventListener("booking:open", openBookingCalendar);
  }, [loadSlots, loading, slots.length]);

  useEffect(() => {
    if (!calendarOpen || !scrollToCalendarOnOpen) return;
    const frame = window.requestAnimationFrame(() => {
      calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollToCalendarOnOpen(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [calendarOpen, scrollToCalendarOnOpen]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return setError("Bitte wähle zuerst eine freie Zeit aus.");

    const form = event.currentTarget;
    const data = new FormData(form);
    const topicGoal = String(data.get("topicGoal") ?? "").trim();
    const parsedStudentAge = Number(studentAge);
    const guardianName = guardianNameValue.trim();
    const guardianEmail = guardianEmailValue.trim();
    const guardianPhone = guardianPhoneValue.trim();
    if (role === "student" && (!Number.isInteger(parsedStudentAge) || parsedStudentAge < 6 || parsedStudentAge > 99)) {
      return setError("Bitte gib dein Alter an.");
    }
    if (isMinorStudent && (!guardianName || !guardianEmail || !guardianPhone)) {
      return setError("Bitte gib die Kontaktdaten eines Elternteils an.");
    }
    setSubmitting(true);
    setError("");
    try {
      const bookingInput = {
        slotStart: selected.start,
        role,
        name: String(data.get("name") ?? "").trim(),
        email: isMinorStudent ? guardianEmail : bookingEmail.trim(),
        phone: isMinorStudent ? guardianPhone : bookingPhone.trim(),
        grade: String(data.get("grade") ?? ""),
        schoolType: "Nicht angegeben",
        topicGoal: topicGoal || "Im Erstgespräch klären",
        studentAge: role === "student" ? parsedStudentAge : undefined,
        guardianName: isMinorStudent ? guardianName : undefined,
        guardianEmail: isMinorStudent ? guardianEmail : undefined,
        guardianPhone: isMinorStudent ? guardianPhone : undefined,
        turnstileToken,
      };
      if (attachment) await api.createBookingRequestWithAttachment(bookingInput, attachment);
      else await api.createBookingRequest(bookingInput);
      setSuccess(true);
      form.reset();
      setSelected(undefined);
      setAttachment(undefined);
      setRole("parent");
      setStudentAge("");
      setBookingEmail("");
      setBookingPhone("");
      setGuardianNameValue("");
      setGuardianEmailValue("");
      setGuardianPhoneValue("");
      await loadSlots();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Die Anfrage konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="booking-container">
      <div className="booking-heading">
        <div className="eyebrow light">Kostenlose Probestunde</div>
        <h2>Wie möchtest du deinen Termin vereinbaren?</h2>
        <p>Such dir den einfachsten Weg aus. Beides ist kostenlos und unverbindlich.</p>
      </div>

      <div className="booking-choices">
        <article className="booking-choice is-primary">
          <span className="choice-icon choice-icon-whatsapp"><WhatsAppIcon /></span>
          <h3>Per WhatsApp anfragen</h3>
          <p>Kurz schreiben und gemeinsam einen passenden Termin finden.</p>
          <WhatsAppCta label="WhatsApp öffnen" className="full-width" />
        </article>
        <article className="booking-choice">
          <span className="choice-icon"><CalendarDays aria-hidden="true" /></span>
          <h3>Freien Termin auswählen</h3>
          <p>Direkt verfügbare Zeiten ansehen und eine Wunschzeit auswählen.</p>
          <button className="button button-primary full-width" type="button" onClick={() => { if (calendarOpen) setCalendarOpen(false); else openCalendar(); }} aria-expanded={calendarOpen} aria-controls="booking-calendar">
            {calendarOpen ? "Freie Zeiten ausblenden" : "Freie Zeiten anzeigen"} {calendarOpen ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
          </button>
        </article>
      </div>

      {calendarOpen && (
        <div className="booking-card booking-calendar" id="booking-calendar" ref={calendarRef}>
          {success ? (
            <div className="success-panel" role="status">
              <CheckCircle2 />
              <h3>Danke, deine Anfrage ist angekommen.</h3>
              <p>Ich prüfe den Termin persönlich und melde mich anschließend bei dir.</p>
              <button className="button button-secondary" type="button" onClick={() => setSuccess(false)}>Weitere Zeit anfragen</button>
            </div>
          ) : (
            <>
              <div className="card-heading">
                <div><span>Freie Termine</span><h3>Wunschzeit auswählen</h3></div>
                <button type="button" className="text-button" onClick={() => void loadSlots()}>Aktualisieren</button>
              </div>

              {loading && <div className="loading-state"><LoaderCircle className="spin" /> Freie Zeiten werden geprüft …</div>}
              {!loading && slots.length === 0 && !error && <div className="empty-state">Momentan sind keine Probestunden innerhalb der nächsten 14 Tage frei.</div>}
              {error && <div className="alert alert-error" role="alert"><AlertCircle /> {error}</div>}

              {!loading && slots.length > 0 && (
                <div className="slot-days">
                  {Object.entries(grouped).map(([day, daySlots]) => (
                    <div className="slot-day" key={day}>
                      <p>{formatSlotDate(daySlots[0].start)}</p>
                      <div className="slot-row">
                        {daySlots.map((slot) => (
                          (() => {
                            const full = slot.remaining <= 0;
                            return <button
                              type="button"
                              key={slot.start}
                              className={`${selected?.start === slot.start ? "slot-button is-selected" : "slot-button"}${full ? " is-full" : ""}`}
                              onClick={() => { if (!full) { setSelected(slot); setError(""); } }}
                              aria-pressed={selected?.start === slot.start}
                              aria-label={`${formatSlotDate(slot.start)}, ${compactSlotTime(slot.start, slot.end)}, ${full ? "ausgebucht" : "frei"}`}
                              disabled={full}
                            >
                              <strong>{compactSlotTime(slot.start, slot.end)}</strong>
                              <small>{full ? "Termin ausgebucht" : "Termin frei"}</small>
                            </button>;
                          })()
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selected && (
                <form className="booking-form" onSubmit={submit}>
                  <div className="selected-summary"><CheckCircle2 /><span><small>Ausgewählte Zeit</small><strong>{formatSlotDate(selected.start)}, {compactSlotTime(selected.start, selected.end)}</strong></span></div>
                  <div className="card-heading compact"><div><span>Fast geschafft</span><h3>Wie erreiche ich dich?</h3></div></div>
                  <fieldset className="role-picker">
                    <legend>Ich buche als</legend>
                    <div className="role-options">
                      <button className={role === "parent" ? "role-choice is-active" : "role-choice"} type="button" aria-pressed={role === "parent"} onClick={() => { setRole("parent"); setStudentAge(""); setError(""); }}>Elternteil</button>
                      <button className={role === "student" ? "role-choice is-active" : "role-choice"} type="button" aria-pressed={role === "student"} onClick={() => { setRole("student"); setStudentAge(""); setError(""); }}>Schüler/in</button>
                    </div>
                  </fieldset>
                  <div className="form-grid two-cols">
                    <label>Dein Name<input name="name" autoComplete="name" required minLength={2} maxLength={80} /></label>
                    {role === "student" && <label>Wie alt bist du?<input name="studentAge" type="number" inputMode="numeric" min="6" max="99" required value={studentAge} onChange={(event) => changeStudentAge(event.target.value)} /></label>}
                    <label>{role === "parent" ? "Für welche Klassenstufe wird Nachhilfe gesucht?" : "In welcher Klasse bist du?"}<select name="grade" required defaultValue=""><option value="" disabled>Bitte wählen</option>{[6, 7, 8, 9, 10, 11, 12, 13].map((grade) => <option key={grade}>Klasse {grade}</option>)}</select></label>
                    {isMinorStudent ? <label>Name eines Elternteils / einer sorgeberechtigten Person<input name="guardianName" autoComplete="section-guardian name" required minLength={2} maxLength={80} value={guardianNameValue} onChange={(event) => setGuardianNameValue(event.target.value)} /></label> : <label>E-Mail<input name="email" type="email" autoComplete="email" required maxLength={160} value={bookingEmail} onChange={(event) => setBookingEmail(event.target.value)} /></label>}
                    {isMinorStudent ? <label>E-Mail eines Elternteils<input name="guardianEmail" type="email" autoComplete="section-guardian email" required maxLength={160} value={guardianEmailValue} onChange={(event) => setGuardianEmailValue(event.target.value)} /></label> : <label className={role === "student" ? "full-row" : undefined}>Telefon<input name="phone" type="tel" autoComplete="tel" required minLength={6} maxLength={40} value={bookingPhone} onChange={(event) => setBookingPhone(event.target.value)} /></label>}
                    {isMinorStudent && <label>Telefon eines Elternteils<input name="guardianPhone" type="tel" autoComplete="section-guardian tel" required minLength={6} maxLength={40} value={guardianPhoneValue} onChange={(event) => setGuardianPhoneValue(event.target.value)} /></label>}
                  </div>
                  {isMinorStudent && <>
                    <p className="form-hint">Da du noch nicht 18 bist, brauche ich für die weitere Abstimmung die Kontaktdaten eines Elternteils.</p>
                  </>}
                  <label>Thema oder Ziel <em>optional</em><textarea name="topicGoal" maxLength={1000} placeholder="Zum Beispiel Parabeln oder Prüfungsvorbereitung …" /></label>
                  <div className="booking-upload">
                    <label className="booking-upload-label" htmlFor="booking-attachment"><FileUp aria-hidden="true" /><span><strong>Aktuelle Aufgabe hochladen</strong><small>PDF, JPG, PNG oder HEIC · maximal 10 MB</small></span></label>
                    <input id="booking-attachment" type="file" accept="application/pdf,image/jpeg,image/png,image/heic,image/heif" onChange={(event) => selectAttachment(event.target.files?.[0])} />
                    {attachment && <div className="booking-upload-selected"><span>{attachment.name}</span><button type="button" aria-label="Datei entfernen" onClick={() => setAttachment(undefined)}><X aria-hidden="true" /></button></div>}
                  </div>
                  <Turnstile onToken={setTurnstileToken} />
                  <button className="button button-primary full-width" type="submit" disabled={submitting}>
                    {submitting ? <><LoaderCircle className="spin" /> Wird gesendet …</> : <>Probestunde unverbindlich anfragen <ChevronRight /></>}
                  </button>
                  <p className="form-hint">Ich bestätige deine Wunschzeit persönlich per E-Mail. Informationen zur Verarbeitung deiner Daten findest du in der <a href={viewUrl("datenschutz")}>Datenschutzerklärung</a>.</p>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
